import {
    COLOR_OR_TOKEN_PATTERN_WITH_RGBA,
    enhanceOptionsSchemaWithDynamicOptions,
    getPattern,
    validAlignments,
    validCellRenderers,
    validColumnAlignments,
    validFontSizes,
    validHeaderVisibilities,
    validSparklineTypes,
    validTextOverflows,
} from '../utils';

export const tableOptionsSchema = enhanceOptionsSchemaWithDynamicOptions({
    backgroundColor: {
        default: '> themes.defaultBackgroundColor',
        description:
            'Specify the color for the background. You might use a data source to apply the color. The default for enterprise light is "#FFFFFF". The default for enterprise dark is "#000000". The default for prisma dark is "#0B0C0E".',
        pattern: COLOR_OR_TOKEN_PATTERN_WITH_RGBA,
        type: 'string',
    },
    columnFormat: {
        description:
            'Lets you format an individual column in a table. Specify a column in a table as a field, followed by a property. For more information, see the Column Format Align - Left example on the Examples page.',
        type: 'object',
        patternProperties: {
            '.*': {
                description: 'Customization for column property',
                type: 'object',
                properties: {
                    align: {
                        description:
                            'Specify the alignment for the whole column. Valid alignments include "left", "center", and "right". You can also pass an array to specify the alignment of each cell. When set to "auto", the column will use the same configuration as tableFormat.align.',
                        type: ['string', 'array'],
                        items: {
                            type: 'string',
                            enum: validColumnAlignments.concat(),
                        },
                        default: 'auto',
                    },
                    cellTypes: {
                        description:
                            'Specify how to render data in cells. Valid cell renders include "TextCell", "ArrayCell", "SparklineCell".',
                        type: 'array',
                        items: {
                            type: 'string',
                            enum: validCellRenderers.concat(),
                        },
                    },
                    data: {
                        description: 'Specify data points to be displayed in the cells of a column.',
                        type: 'array',
                        items: {
                            oneOf: [
                                { type: 'string' },
                                { type: 'number' },
                                {
                                    type: 'array',
                                    items: { type: ['string', 'number'] },
                                },
                            ],
                        },
                    },
                    footer: {
                        description:
                            'DSL expression to format footer values for this column. Example: "> footer | seriesByName(\\"FieldName\\") | formatByType(unitConfig)". The footer DataFrame contains a 1D series with [total, percentage] for each column.',
                        type: 'string',
                    },
                    headerAlign: {
                        description:
                            'Specify the header alignment for the whole column. Valid alignments include "left", "center", and "right".  When set to "auto", the column will use the same configuration as tableFormat.headerAlign',
                        type: 'string',
                        default: 'auto',
                        pattern: getPattern(validColumnAlignments as unknown as string[]),
                    },
                    rowBackgroundColors: {
                        description:
                            'Specify a list of colors, in hexadecimal code, for the background color of each column of the table. For example, ["#2C333B", "32373D"].',
                        type: 'array',
                        items: {
                            type: 'string',
                            pattern: COLOR_OR_TOKEN_PATTERN_WITH_RGBA,
                        },
                    },
                    rowColors: {
                        description:
                            'Specify a list of colors, in hexadecimal code, for the text color of the cells of a column. For example, ["#2C333B", "32373D"].',
                        type: 'array',
                        items: {
                            type: 'string',
                            pattern: COLOR_OR_TOKEN_PATTERN_WITH_RGBA,
                        },
                    },
                    sparklineAreaColors: {
                        description:
                            'Specify a list of colors, in hexadecimal code, for the sparkline area of each column. For example, ["#2C333B", "32373D"].',
                        type: 'array',
                        items: {
                            type: 'string',
                            pattern: COLOR_OR_TOKEN_PATTERN_WITH_RGBA,
                        },
                    },
                    sparklineColors: {
                        description:
                            'Specify a list of colors, in hexadecimal code, for the sparkline stroke of each column. For example, ["#2C333B", "32373D"].',
                        type: 'array',
                        items: {
                            type: 'string',
                            pattern: COLOR_OR_TOKEN_PATTERN_WITH_RGBA,
                        },
                    },
                    sparklineTypes: {
                        description:
                            'Choose either area or line formatting for the Sparkline. Valid sparkline types include "line" and "area".',
                        type: 'array',
                        items: {
                            type: 'string',
                            enum: validSparklineTypes.concat(),
                        },
                    },
                    sparklineNullValueDisplay: {
                        description:
                            'Specify the handling of null values for sparkline cells. "zero" treats null as 0, "gaps" shows breaks in the line, "connect" connects points across nulls.',
                        type: 'string',
                        default: 'gaps',
                        pattern: getPattern(['gaps', 'zero', 'connect']),
                    },
                    textOverflow: {
                        description:
                            'Controls how the text should be truncated within a particular column. Valid types include "anywhere", "break-word", "ellipsis".  Columns and Headers are "break-word" styled by default when "textOverflow" is not configured.',
                        type: 'string',
                        pattern: getPattern(validTextOverflows as unknown as string[]),
                    },
                    width: {
                        description:
                            'Specify, in pixels, the width of the column. The default displays a minimum width of at least 90, unless the table needs to filled further. The minimum width is 30px.',
                        type: 'number',
                        minimum: 30,
                    },
                },
            },
        },
    },
    count: {
        description: 'Specify the maximum number of rows to display.',
        type: 'number',
        default: 10,
    },
    font: {
        default: 'proportional',
        description: 'Specify the font style to use for table content.',
        type: 'string',
        pattern: getPattern(['proportional', 'monospace']),
    },
    fontSize: {
        default: 'default',
        description:
            'Specify the font size to use for table content. Font sizes range from extra small (10px) to large (16px) with the default being 14px.',
        type: 'string',
        pattern: getPattern(validFontSizes as unknown as string[]),
    },
    headers: {
        default: '> table | getField()',
        description:
            "Specify an array of headers to display on the table. Though these can be statically listed, it's best to use the dynamic default, as it will return the fields of your search.",
        type: 'array',
        items: {
            type: 'string',
        },
    },
    headerVisibility: {
        default: 'inline',
        description: 'Specify how to display the table header row.',
        pattern: getPattern(validHeaderVisibilities as unknown as string[]),
        enum: validHeaderVisibilities.concat(),
        type: 'string',
    },
    paginateDataSourceKey: {
        default: 'primary',
        description: 'Specify the data source key for pagination and sorting.',
        type: 'string',
    },
    showFooterPercentages: {
        default: false,
        description:
            'Specify whether to show a row at the bottom of the table that displays percentage values for applicable columns.',
        type: 'boolean',
    },
    showFooterTotals: {
        default: false,
        description:
            'Specify whether to show a row at the bottom of the table that displays total values for applicable columns.',
        type: 'boolean',
    },
    showColumnAsExpandedJson: {
        description:
            'Specify row expansion content as stringified JSON. Provide either a DSL expression that resolves to an array of stringified JSON values, or a static array of stringified JSON values. Each item is rendered in the matching expanded row with JSONTree.',
        type: ['string', 'array'],
        items: {
            type: 'string',
        },
    },
    showInternalFields: {
        default: true,
        description:
            'Specify whether to show internal fields that start with an underscore. The _time field will still be shown if false.',
        type: 'boolean',
    },
    showRowNumbers: {
        default: false,
        description: 'Show row numbers in the first column.',
        type: 'boolean',
    },
    table: {
        default: '> primary',
        description: 'Two dimensional array of data to be displayed in the table.',
        type: 'array',
        items: {
            type: 'array',
            items: {
                oneOf: [
                    { type: 'string' },
                    { type: 'number' },
                    { type: 'array', items: { type: ['string', 'number'] } },
                ],
            },
        },
    },
    tableFormat: {
        description: 'Lets you set global options for a table.',
        type: 'object',
        properties: {
            align: {
                default: '> table | type() | matchValue(tableAlignByType)',
                description:
                    'Specify alignment for the whole table. Valid alignments include "left", "center", and "right". You can also pass a 2 dimensional array to specify the alignment of each cell. By default, the table will be aligned by type.',
                type: ['string', 'array'],
                items: {
                    type: 'array',
                    items: {
                        type: 'string',
                        enum: validAlignments.concat(),
                    },
                },
            },
            cellTypes: {
                default: '> table | type() | matchValue(tableCellTypeByType)',
                description:
                    'Specify how to render data in cells. Valid cell renders include "TextCell", "ArrayCell", "SparklineCell".',
                type: 'array',
                items: {
                    type: 'array',
                    items: {
                        type: 'string',
                        enum: validCellRenderers.concat(),
                    },
                },
            },
            data: {
                description:
                    'Two dimensional data to be displayed in the table. This is usually formatted data instead of raw data from search.',
                type: 'array',
                items: {
                    type: 'array',
                    items: {
                        oneOf: [
                            { type: 'string' },
                            { type: 'number' },
                            {
                                type: 'array',
                                items: { type: ['string', 'number'] },
                            },
                        ],
                    },
                },
            },
            headerAlign: {
                description:
                    'Specify the header alignment for the whole table. Valid alignments include "left", "center", and "right".',
                type: 'string',
                default: 'left',
                pattern: getPattern(validAlignments as unknown as string[]),
            },
            headerBackgroundColor: {
                default: '> themes.defaultHeaderBackgroundColor',
                description:
                    'Specify the table header background color using a hexadecimal code. For example, "#FFFFFF".',
                pattern: COLOR_OR_TOKEN_PATTERN_WITH_RGBA,
                type: 'string',
            },
            headerColor: {
                description:
                    'Specify the table header text color using a hexadecimal code. For example, "#FFFFFF".',
                pattern: COLOR_OR_TOKEN_PATTERN_WITH_RGBA,
                type: 'string',
            },
            rowBackgroundColors: {
                default: '> table | seriesByIndex(0) | pick(tableAltRowBackgroundColorsByTheme)',
                description:
                    'Specify a list of colors, in hexadecimal code, for the background color of each row of the table. For example, ["#2C333B", "32373D"].',
                type: 'array',
                items: {
                    type: 'array',
                    items: {
                        type: 'string',
                        pattern: COLOR_OR_TOKEN_PATTERN_WITH_RGBA,
                    },
                },
            },
            rowColors: {
                description:
                    'Specify a list of colors, in hexadecimal code, for the text color of each row of the table. For example, ["#2C333B", "32373D"].',
                type: 'array',
                items: {
                    type: 'array',
                    items: {
                        type: 'string',
                        pattern: COLOR_OR_TOKEN_PATTERN_WITH_RGBA,
                    },
                },
            },
            sparklineAreaColors: {
                description:
                    'Specify a list of colors, in hexadecimal code, for the sparkline area of each row of the table. For example, ["#2C333B", "32373D"].',
                type: 'array',
                items: {
                    type: 'array',
                    items: {
                        type: 'string',
                        pattern: COLOR_OR_TOKEN_PATTERN_WITH_RGBA,
                    },
                },
            },
            sparklineColors: {
                description:
                    'Specify a list of colors, in hexadecimal code, for the sparkline stroke of each row of the table. For example, ["#2C333B", "32373D"].',
                type: 'array',
                items: {
                    type: 'array',
                    items: {
                        type: 'string',
                        pattern: COLOR_OR_TOKEN_PATTERN_WITH_RGBA,
                    },
                },
            },
            sparklineTypes: {
                description: 'Choose either area or line formatting for the Sparkline.',
                type: 'array',
                items: {
                    type: 'array',
                    items: {
                        type: 'string',
                        enum: validSparklineTypes.concat(),
                    },
                },
            },
            sparklineNullValueDisplay: {
                description:
                    'Specify the handling of null values for sparkline cells. "zero" treats null as 0, "gaps" shows breaks in the line, "connect" connects points across nulls.',
                type: 'string',
                default: 'gaps',
                pattern: getPattern(['gaps', 'zero', 'connect']),
            },
            textOverflow: {
                description:
                    'Specify the text overflow for the whole table. Valid overflows include "break-word", "anywhere", and "ellipsis". Default for Column and Headers is "break-word".',
                type: 'string',
                pattern: getPattern(validTextOverflows as unknown as string[]),
            },
        },
    },
});
